import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { bowlReturnHref, assignmentCardChoice } from '../lib/assignmentSetup.js'
import PageHeader from '../components/PageHeader.jsx'
import { COLLEGE_BODIES } from '../lib/collegeAthleticBodies.js'
import { CANADA_BODIES, PRO_BODIES } from '../lib/otherAthleticBodies.js'
import { CHEER_BODIES, CHEER_UNSPECIFIED } from '../lib/cheerAthleticBodies.js'
import { isCheerOrDance } from '../lib/activities.js'
import ChampionshipFinder from '../features/integrity/ChampionshipFinder.jsx'
import { api } from '../lib/api.js'
import { useChampionships } from '../features/command-center/useChampionships.js'
import { auditSet, bucketFor } from '../lib/championshipAudit.js'
import { STATE_BODIES, allEntities, markFor } from '../lib/stateAthleticBodies.js'
import { useAccess } from '../lib/access.jsx'
import { ACTIONS } from '../lib/rbac.js'
import BodyChampionships from '../features/integrity/BodyChampionships.jsx'
import DefectPanel from '../features/integrity/DefectPanel.jsx'
import { championshipsFor, zeroKind, rollupFor, peerBaseline, coverageSignal } from '../lib/bodyChampionships.js'
import { searchBodies, explainNoMatch } from '../features/command-center/bodySearch.js'
import { bodyCoverage } from '../features/command-center/bodyCoverage.js'
import {
  assignmentByKey, assignmentTotals, orderBodies, SORT_MODES,
} from '../features/command-center/bodyAssignment.js'
import { membersFor, memberTotals } from '../lib/bodyMembers.js'
import BodyRoster from '../features/command-center/BodyRoster.jsx'
import RosterSetup from '../features/command-center/RosterSetup.jsx'
import BowlSetupGuide from '../features/bowl/BowlSetupGuide.jsx'
import BodyTrades, { TradeRollup } from '../features/trades/BodyTrades.jsx'
import BodyPerfection from '../features/command-center/BodyPerfection.jsx'
import BodyMarketInventory from '../features/command-center/BodyMarketInventory.jsx'
import DerivedBodies from '../features/command-center/DerivedBodies.jsx'
/* ⭐ NO DEAD-END FLAGS. Brian, 2026-10-15: "if it's not something you can fix
   it, surface it but I need a cta to actually fix it. Not just tell me and not
   have a place to actually fix it."

   These four imports are the whole mechanism. `bodyIssues` is the only place
   a flag on this page may be declared, and it refuses at module load to hold
   one without a control; `IssueFix` renders a flag together with the controls
   the model resolved, so a renderer cannot drop one; `BodyFixPanel` carries
   the page-level issues and prints the assertion that none is control-less;
   `useVerificationBodies` is what lets a card say that nothing has ever
   checked this body against its own site. */
import { cardIssues } from '../features/command-center/bodyIssues.js'
import IssueFix from '../features/command-center/IssueFix.jsx'
import BodyFixPanel from '../features/command-center/BodyFixPanel.jsx'
import { useVerificationBodies, verificationFor } from '../features/command-center/useVerificationBodies.js'
import { derivedBodies } from '../features/command-center/derivedBodies.js'
import { REGISTRY_ENTRIES, REGISTRY_LABELS, REGISTRY_ENTITY_COUNT } from '../lib/bodyRegistryIndex.js'
import styles from './GoverningBodies.module.css'

/**
 * STATE GOVERNING BODIES — every state entity, on the board.
 *
 * Brian: "every state entity should have a place on our board and should look
 * like a branded experience and include the state governing body official logo."
 *
 * Every high-school state championship SR sells into is sanctioned by one of
 * these. They are the authority behind the record — UIL decides Texas football
 * has twelve divisions and that swimming is "4A & Below, 5A, 6A", and getting
 * that wrong left SR holding six meets that do not happen.
 *
 * ⚠️ THE LOGOS ARE NOT DRAWN FROM THEIR WEBSITES, ON PURPOSE.
 *
 * The slot is real and wired, and a card starts showing a mark the day one is
 * licensed and hosted by SR. What this page will not do is hot-link 51 logos:
 *
 *   · OWNED ASSETS — a visual asset must be owned and hosted in the system of
 *     record. A URL to ahsaa.com in our board is a dead link waiting to happen,
 *     and a dead link poisons the record. Top Tier Teams already works this way.
 *   · THESE MARKS ARE NOT OURS — an association's logo is its trademark. HS
 *     marks are already flagged IP-blocked internally and a governing body is
 *     the same class of rights-holder. Showing one needs permission, not a file.
 *
 * `markFor()` enforces it rather than trusting the registry: an http(s) logo
 * value is REFUSED and falls back to SR-set type, so the rule cannot be broken
 * by editing data.
 *
 * ⚠️ IT IS NOT A LOGO WALL. Each card carries what we actually hold for that
 * state — championship records and the integrity defects on them — read from
 * the same feed the Command Center uses. A directory that only listed names
 * would be decoration; this makes the body the way into the state's work.
 */

const nf = (n) => (typeof n === 'number' ? n.toLocaleString() : '—')

/* Named constants so check-shipped-features.mjs can assert the sentence that
 * ships rather than a fragment the JSX transform happened to leave intact. */
export const BLIND_SPOT_LINE =
  'governing bodies hold records and have no registry entry, so no card on this page can hold their'
/* ⚠️ SUPERSEDED 2026-10-14, AND THE OLD SENTENCE IS KEPT HERE RATHER THAN
 * DELETED, because it is the thing that was fixed. It read:
 *
 *   'This is the Pac-12 defect, and it is not visible from anywhere on this page.'
 *
 * True from 2.74.0 until today, when the bodies got cards on this page. Leaving
 * it rendering would have been the page describing a defect it no longer has;
 * leaving it ASSERTED in check-shipped-features.mjs would have forced the defect
 * to stay on screen to pass a gate. Both are amended, on the record.
 *
 * ⚠️ AND THE REPLACEMENT DOES NOT SAY "FIXED". The records are reachable; what
 * the registry holds has not changed, four different populations are in that
 * number, and three keys are still blocked on a data repair. A banner claiming
 * the problem is closed would be the next false green. */
export const BLIND_SPOT_FIXED =
  'They are the Pac-12 shape — records, sometimes a rep, and no card. They are carded now, '
  + 'from the object rather than from the registry.'

/* ⚠️ THE CAVEAT THAT MUST TRAVEL WITH EVERY BLANK ASSIGNMENT STAMP.
 *
 * `BodyCoverage.jsx` ships `REP_UNAVAILABLE` and check-shipped-features.mjs
 * already gates its sentence, because a rep field that is blank and a body
 * nobody has been given look identical on screen and only one of them is true.
 * A second surface showing the same blank without the same caveat is precisely
 * the defect that gate was written against, one page over.
 *
 * The older full-object measurement is retained as explicitly historical
 * evidence, not a current assignment total or an active-allocation receipt. */
export const ASSIGNMENT_CAVEAT =
  'A blank rep field and a body nobody has been given look identical on a card, so this stamp '
  + 'states which it is. Historical snapshot — 2026-10-13T21:09Z, not a current count: a full read of '
  + '15,814 records: 13,415 name an assigned rep and 2,399 do not, which is 15.2% of the '
  + 'object in that snapshot. These figures do not establish today’s assignment coverage or an '
  + 'active allocation run. Current cards describe only the records returned by this session’s read.'

/* ⚠️ THE RECONCILIATION, AND ITS FAILURE BRANCH, BOTH SHIPPED. A breakdown that
 * can only report good news is not a reconciliation. Exported as named
 * constants because check-shipped-features.mjs greps the BUILT bundle and the
 * JSX transform splits multi-line element text — only a named constant survives
 * as one literal. Same reason BodyCoverage.jsx does it. */
export const ASSIGNMENT_RECONCILED =
  'Assigned plus unassigned is exactly the number of records counted on the cards above, so '
  + 'nothing has been silently dropped from this assignment breakdown.'
export const ASSIGNMENT_RECONCILE_FAILED = 'RECONCILIATION FAILED.'

/* The card-level version. A stamp whose breakdown does not add up to the
 * `championships` figure beside it is the thing this lane was asked to fix, so
 * it is rendered as a defect on that card rather than quietly rounded away. */
export const ASSIGNMENT_CARD_MISMATCH =
  'the assignment breakdown on this card does not add up to the championships counted beside '
  + 'it, so one of the two numbers is wrong and neither should be quoted'

/* ⚠️ THE CATEGORY TEST LIVES IN ONE PLACE, `lib/cheerSports.js`.
   This page used to carry its own `/cheer|dance|spirit|stunt|drill|pom/i`, and
   that regex was WRONG in two ways that only showed up when it was measured
   against the object: it matched "Pom" inside "Pommel Horse" and pulled a
   gymnastics record into the category, and it read the championship NAME as
   well as the sport, which let a TSSAA title reading "Varsity Spirit" in too.
   A second copy of a rule is a second copy of every bug in it. */

/* The registry each tab draws from — the same map `LEVELS` builds inside the
 * component, hoisted so a deep link can find WHICH tab holds a body. */
const TAB_SOURCES = {
  hs: STATE_BODIES, college: COLLEGE_BODIES, canada: CANADA_BODIES, pro: PRO_BODIES, cheer: CHEER_BODIES,
}
/** The tab whose registry holds this body; the records-derived tab otherwise. */
const tabForBody = (body) => {
  for (const [k, src] of Object.entries(TAB_SOURCES)) {
    if (searchBodies(allEntities(src), body).length) return k
  }
  return 'derived'
}

export default function GoverningBodies() {
  const { can, why, role, canManageAssignments, assignmentPermissionState } = useAccess()
  const allowed = can(ACTIONS.CC_VIEW)
  const canPull = can(ACTIONS.SYSTEM_RUN)
  const { data, status, complete, loadedCount } = useChampionships()
  const [q, setQ] = useState('')
  /* ⭐ `/governing-bodies?body=<name>` — THE DOOR A FLAGGED TRADE USES
   * (2026-10-19). "No rep for this body x state x sport" sends the reader here,
   * and the fix is the roster control on that body's card — so the card is
   * found for them rather than left for them to search for. */
  const [params, setParams] = useSearchParams()
  const focusedSetup = params.get('setup') === 'assignments'
  const [cardNavigation, setCardNavigation] = useState(0)
  /* ⚠️ AND THE RIGHT TAB. Measured 2026-10-19 on the first click-through: a
   * door to "Big Sky" filled the search on the HIGH SCHOOL tab, where no Big
   * Sky card exists — a door into an empty room. So the tab is chosen from the
   * registry the body is actually in, falling back to the records-derived tab,
   * which carries a card for every body that holds records. See tabForBody. */
  useEffect(() => {
    const b = params.get('body')
    if (b) { setQ(b); setLevel(focusedSetup || params.get('level') === 'college' ? 'college' : tabForBody(b)) }
    else { setQ(''); setLevel(Object.hasOwn(TAB_SOURCES, params.get('level')) ? params.get('level') : 'hs') }
  }, [params, focusedSetup])
  const pickBody = useCallback((body) => {
    const name = String(body || '').trim()
    if (!name) return
    setParams(previous => {
      const next = new URLSearchParams(previous)
      next.set('body', name)
      next.set('level', focusedSetup ? 'college' : tabForBody(name))
      return next
    })
    // Repeated clicks on the same card still move focus, without a write.
    setCardNavigation(n => n + 1)
  }, [setParams, focusedSetup])
  /* Marks live in app_kv on the backend, merged over the registry here — so
   * adding one is an operator action, not a deploy. */
  const [marks, setMarks] = useState({})
  const [pull, setPull] = useState({ key: '', url: '', busy: false, error: '', ok: '' })
  /* Pulling 60+ marks one card at a time is not a workflow. This walks every
   * entity that has a verified source and no hosted copy yet, SEQUENTIALLY —
   * 60 parallel uploads into File Manager is how you get rate-limited into a
   * partial result nobody can reconstruct. */
  const [bulk, setBulk] = useState({ running: false, done: 0, total: 0, failed: [], note: '' })

  const loadMarks = useCallback(() => {
    api.brandMarks().then((d) => setMarks(d?.marks || {})).catch(() => { /* a card without a mark still works */ })
  }, [])
  useEffect(() => { if (allowed) loadMarks() }, [allowed, loadMarks])

  const ingest = async (key, label) => {
    setPull((p) => ({ ...p, busy: true, error: '', ok: '' }))
    try {
      await api.brandMarkIngest({ key, label, sourceUrl: pull.url })
      setPull({ key: '', url: '', busy: false, error: '', ok: `${label} mark hosted in HubSpot.` })
      loadMarks()
    } catch (e) {
      setPull((p) => ({ ...p, busy: false, error: e?.body?.detail?.message || e?.body?.message || e?.message || 'Could not pull that mark.' }))
    }
  }

  /* ⚠️ TWO BUGS MADE THIS BUTTON DO NOTHING, VISIBLY OR OTHERWISE.
   *
   * Brian: "if you click pull every mark into hubspot - it doesnt seem to do
   * anything."
   *
   * 1. IT WALKED `STATE_BODIES` HARDCODED, whatever tab you were on. Press it
   *    on College, Canada or Pro and it iterated high-school entities — so the
   *    marks you were looking at were never candidates.
   *
   * 2. AN EMPTY TODO PRODUCED SILENCE. If every eligible mark was already
   *    hosted, `todo` was empty, the loop body never ran, and the two setBulk
   *    calls fired back to back — button label flickers and returns, no
   *    message, no result. Indistinguishable from a dead button.
   *
   * That is the silent-zero pattern in a control: doing nothing and SAYING
   * nothing are the same thing to the person clicking. It now reports what it
   * found before it starts, and always leaves a sentence behind. */
  /* ⚠️ TAKES THE LEVEL AS ARGUMENTS rather than closing over `SOURCE` and
   * `level`, which are declared 70 lines BELOW this. That worked only because
   * a click happens after render finishes — a temporal-dead-zone escape by
   * timing rather than by design, and the kind of thing that breaks the first
   * time somebody calls it from an effect. */
  const pullAll = async (levelSource, levelKey) => {
    const all = allEntities(levelSource)
    const withSource = all.filter((e) => e.logoSource)
    const todo = withSource.filter((e) => !marks[e.key])
    const already = withSource.length - todo.length
    const noSource = all.length - withSource.length

    if (!todo.length) {
      setBulk({
        running: false, done: 0, total: 0, failed: [],
        note: withSource.length
          ? `Nothing to pull — all ${already} ${LEVEL_LABEL[levelKey]} marks with a published source are already hosted in HubSpot. ${noSource} bodies publish no logo we can use.`
          : `No ${LEVEL_LABEL[levelKey]} body on this tab has a published logo source, so there is nothing to pull.`,
      })
      return
    }

    setBulk({ running: true, done: 0, total: todo.length, failed: [], note: '' })
    const failed = []
    for (let i = 0; i < todo.length; i += 1) {
      const e = todo[i]
      try { await api.brandMarkIngest({ key: e.key, label: e.acronym, sourceUrl: e.logoSource }) }
      catch (err) { failed.push(`${e.key}: ${err?.body?.detail?.message || err?.body?.message || err?.message || 'failed'}`) }
      setBulk({ running: true, done: i + 1, total: todo.length, failed: [...failed], note: '' })
    }
    setBulk({
      running: false, done: todo.length, total: todo.length, failed,
      note: `Pulled ${todo.length - failed.length} of ${todo.length}. ${already} were already hosted.`,
    })
    loadMarks()
  }

  /* ⚠️ THE ORPHANS. A state-level record with no state lands in the
     "(state missing)" bucket, and no card has that state, so it was counted in
     the audit and shown to nobody. It gets its own door below. */
  const orphans = useMemo(
    () => (data || []).filter((c) => bucketFor(c) === '(state missing)'),
    [data],
  )

  /* ⚠️ THE CHEER ORPHANS — the same defect, one field over.
     292 cheer and dance records carry sr_conference = 'Unspecified', which is
     not an organisation. No registry entry can ever give them a card, because
     the record does not say who sanctioned it. That makes them exactly the
     "(state missing)" case at body level, and they get the same treatment: a
     door that opens them rather than a number nobody can reach — or, worse,
     silence that reads as "the tab covers everything". Resolving them is a
     data repair and needs a human. */
  /* ⚠️ THE CATEGORY, ONCE, FOR BOTH THE DOOR AND THE LENS. Computing it twice
     is how two numbers that describe the same thing start disagreeing. */
  const cheerUniverse = useMemo(
    () => (data || []).filter(isCheerOrDance),
    [data],
  )

  const unspecified = useMemo(
    () => cheerUniverse.filter(
      (c) => String((c?.raw || c || {}).sr_conference ?? '').trim() === CHEER_UNSPECIFIED,
    ),
    [cheerUniverse],
  )

  /* ⚠️ HIGH SCHOOL AND COLLEGE ARE DIFFERENT BOARDS, not one list with a
     filter on it. Brian: "Governing bodies and school directors should be
     broken up into hs and college."

     They were conflated — this page held 51 state high-school associations and
     nothing else, so a college conference had no home on the board at all,
     despite being where a large share of ring orders sit. The two levels have
     different questions attached (a state's championship coverage vs an
     association's divisions), so they get different views rather than a shared
     one that answers neither well. */
  /* The card the operator has opened. Brian: "we should be able to click on
     them and see when they are and who they are with then in the command
     centre." One state, because two drill-ins open at once is never wanted. */
  const LEVEL_LABEL = { hs: 'high-school', college: 'college', canada: 'Canadian', pro: 'professional', cheer: 'cheer and dance' }
  const [open, setOpen] = useState(null)
  /* ⚠️ THE DEFECT COUNT NOW OPENS. It was a bare number with no door — the
     audit has exported the offending records all along and this page never
     called for them, so the figure reported a problem you could not reach. */
  const [defectsOpen, setDefectsOpen] = useState(null)
  /* The "(state missing)" drawer: the same panel, handed the rows directly. */
  const [orphansOpen, setOrphansOpen] = useState(false)
  /* ⚠️ THE LENS IS NOT A CHEER FEATURE. It holds the ACTIVITY it opened on,
     and cheer is only the first preset — Brian: "it can also be done crossing
     every other governing body by sport or activity". Null means closed. */
  /* ⚠️ ONE DRAWER, TWO ENTRY POINTS. Free text is the mechanism and an
     activity preset is a shortcut into it, so both open the same finder rather
     than two views that can disagree about a count. Null means closed. */
  const [finder, setFinder] = useState(null)
  /* The championship query, kept apart from `q` — one finds a body, the
     other finds a title, and sharing one box for both is how "football"
     returned an empty board. */
  const [champQ, setChampQ] = useState('')
  const [level, setLevel] = useState('hs')
  /* ⚠️ FOUR LEVELS, NOT TWO.
     Brian: "we also need a button just to show canadian hs or canadian
     colleges" … "wouldnt it be prudent to do all professional like this".

     Both were already ON THE OBJECT with nowhere on the board to look them up
     — 650 records at sr_segment = 'Canada' and 61 at sr_level =
     'Professional'. A record nobody can find is barely better than one that
     does not exist, and the Hurricanes are a live account. */
  /* ⚠️ FIVE LEVELS NOW — AND THE FIFTH IS THE BIGGEST.
     Brian has asked for cheer and dance twice. Measured against the live
     object rather than assumed: Varsity Spirit holds 1,354 records against
     California Interscholastic Federation's 1,138, making it the LARGEST
     governing body SR has, and it had no card anywhere on this page. 1,936 of
     2,228 cheer and dance records rendered nowhere at all.

     It is its own tab for the same reason Professional is: Varsity Spirit
     crowns at All-Star, Dance, HS State and college level simultaneously, so
     no existing tab fits it and filing it under one would fragment the largest
     body in the object across three boards. */
  const LEVELS = {
    hs: STATE_BODIES,
    college: COLLEGE_BODIES,
    canada: CANADA_BODIES,
    pro: PRO_BODIES,
    cheer: CHEER_BODIES,
  }
  const SOURCE = LEVELS[level] || STATE_BODIES

  /* ⚠️ THE OLD FILTER COULD NOT FIND THE PAC-12, AND THAT IS WHAT FAILED BRIAN
     IN FRONT OF HIS TEAM. It was a raw substring test:

         e.acronym.toLowerCase().includes(q.trim().toLowerCase())

     The stored acronym is "Pac-12". Type "PAC12" and you are asking whether
     "pac-12" contains "pac12" — it does not, because of the hyphen, so the board
     answered that the conference does not exist. Same hole for "A10" vs
     "Atlantic 10", "B1G" vs "Big Ten", "GMAC" vs "G-MAC", "NE10" vs
     "Northeast-10". `searchBodies` strips punctuation on BOTH sides, resolves
     dead names through the rename map (so "American Athletic Conference" still
     finds the American), and returns best matches first. */
  const hits = useMemo(() => searchBodies(allEntities(SOURCE), q), [q, SOURCE])
  const entities = useMemo(() => hits.map((h) => h.entity), [hits])
  useEffect(() => {
    if (!params.get('body') || q !== params.get('body')) return undefined
    const frame = requestAnimationFrame(() => {
      const target = level === 'derived'
        ? [...document.querySelectorAll('[data-derived-body]')].find(el => el.dataset.derivedBody === q)
        : [...document.querySelectorAll('[data-gb-card]')].find(el => el.dataset.gbCard === hits[0]?.entity?.key)
      const destination = target || document.querySelector('[data-gb-search="1"]')
      destination?.scrollIntoView({ block: 'start' })
      destination?.focus({ preventScroll: true })
    })
    return () => cancelAnimationFrame(frame)
  }, [params, q, level, hits, status, cardNavigation])

  /* The rename, if the query reached a card through a former name — surfaced so
     an unfamiliar card is explained rather than merely shown. */
  const renamed = useMemo(() => hits.find((h) => h.renamed)?.renamed || null, [hits])
  /* Why nothing matched. Computed only when nothing matched, because it walks
     every entity once per query TERM. */
  const noMatch = useMemo(
    () => (q.trim() && !hits.length ? explainNoMatch(allEntities(SOURCE), q) : null),
    [q, hits.length, SOURCE],
  )

  /* ⚠️ THE PEER BASELINE, ONE PASS PER TAB, NOT ONE PER CARD PER RENDER.
     `peerBaseline` runs `championshipsFor` for every entity on the tab, and
     calling it inside the card loop would make that O(bodies x bodies x 15k).
     It is keyed on the FULL entity list rather than the filtered one, so a
     search does not change any card's peer median — a body's coverage is a fact
     about the body, not about what somebody typed in a box. */
  const baseline = useMemo(
    () => peerBaseline(allEntities(SOURCE), data || []),
    [SOURCE, data],
  )

  /* ⚠️ DERIVED FROM THE ROSTERS, NEVER TYPED. Every number on this topline is
     computed from the data it describes, so a harvest that adds a body or a
     thousand schools moves the count with no edit here — and a number can never
     drift out of step with the list under it.

     ⚠️ AND IT SITS ABOVE THE PERMISSION EARLY-RETURN ON PURPOSE. Placed below
     it, this is a CONDITIONAL HOOK — React requires every hook to run in the
     same order on every render, and a viewer without CC_VIEW returns before
     reaching it. `npm run lint` runs before `vite build` and failed the Netlify
     deploy on exactly this (react-hooks/rules-of-hooks), which is the gate
     working: it never reached production. */
  const roll = useMemo(() => memberTotals(allEntities(SOURCE), level), [SOURCE, level])

  /* ⚠️ EVERY CARD'S BADGE, FROM THE BODY'S OWN RECORDS. The badge used to read
     the STATE audit while everything else on the card read the body, so it
     printed California's 130 on all eleven CIF cards and nothing on College,
     Canada or Pro (a conference name is not a state). `auditSet` over the
     body's rows is exactly what the drawer lists, on every tab.

     The reconciliation is kept beside it: the sum of the badges against the
     number of DISTINCT records behind them. A record matched by two bodies
     counts on both cards, and saying how many is what stops a summed total
     being read as the size of the problem. */
  const audits = useMemo(() => {
    const byKey = new Map()
    let sum = 0
    const distinct = new Set()
    for (const e of allEntities(SOURCE)) {
      const rows = championshipsFor(e, data || []).rows
      const a = auditSet(rows)
      byKey.set(e.key, a)
      sum += a.blocking
      for (const it of a.items) if (it.blocking) distinct.add(String(it.raw.id || it.raw.hs_object_id || it.rec?.id || ''))
    }
    return { byKey, sum, distinct: distinct.size }
  }, [SOURCE, data])

  /* ⚠️ WHO IS ASSIGNED, ONE PASS PER TAB, FROM THE CARD'S OWN ROWS.
   *
   * Brian: *"every governing body should include a stamp of who is assigned."*
   * The page showed no rep anywhere — not on a card, not in a drawer — while
   * the assignment sat on every record it was already counting.
   *
   * ⚠️ IT IS KEYED AND SHAPED EXACTLY LIKE `audits` AND `baseline` ABOVE, and
   * for the identical reason stated there: `assignmentByKey` runs
   * `championshipsFor` once per entity, so calling it inside the card loop
   * would be O(bodies x bodies x 15k) on every render. `assignByKey.get(e.key)`
   * inside the loop is a Map read.
   *
   * ⚠️ AND IT REUSES THAT JOIN RATHER THAN INVENTING A SECOND ONE. If the stamp
   * counted a different set than `tally.held`, the card would print a
   * breakdown that does not add up to the number beside it — see
   * ASSIGNMENT_CARD_MISMATCH, which is rendered when that happens rather than
   * hidden. */
  const assignByKey = useMemo(
    () => assignmentByKey(allEntities(SOURCE), data || []),
    [SOURCE, data],
  )

  /* 🔴 THE SORT THIS PAGE HAS NEVER HAD. Card order was `searchBodies()`
   * relevance and nothing else — there was no sort state, no control, no
   * comparator.
   *
   * ⚠️ AND IT IS NOT `makeComparator('owner')`. That one's own comment reads
   * "Blank owners are the expected state right now — always sort last", in BOTH
   * directions, which would leave the unassigned bodies unreachable by sorting.
   * Brian ruled that out in as many words: unassigned must be a visible,
   * sortable state. `compareBodies` in bodyAssignment.js treats it as a VALUE,
   * so one direction brings it to the top. */
  const [sort, setSort] = useState({ mode: 'records', dir: 'asc' })

  const ordered = useMemo(
    () => orderBodies(entities, assignByKey, {
      mode: sort.mode,
      dir: sort.dir,
      /* An assignment sort over a feed that has not loaded would order every
       * card by the same zero and present it as an answer. */
      loaded: status !== 'loading' && (data || []).length > 0,
    }),
    [entities, assignByKey, sort.mode, sort.dir, status, data],
  )

  /* The reconciliation for the cards actually on screen. */
  const assignTotals = useMemo(
    () => assignmentTotals(assignByKey, ordered.entities),
    [assignByKey, ordered.entities],
  )

  /* ⚠️ WHAT THIS PAGE CANNOT SHOW, COUNTED ON THIS PAGE.
   *
   * Every number above is derived from the registry and is therefore blind in
   * exactly one direction: `championshipsFor` walks the universe ONCE PER
   * ENTITY, so a record whose `sr_conference` matches no entity is never pushed
   * into any card's rows and is counted by nothing here — not as a zero, not as
   * an empty card, not at all. That is not a rendering choice, it is the shape
   * of the derivation, and it is how the Pac-12 held six championships assigned
   * to Philip while the board said the conference did not exist.
   *
   * So the one figure this page must carry is the size of its own blind spot,
   * with a door out of it. Measured live, never typed. */
  const blind = useMemo(() => bodyCoverage(data || []).totals, [data])

  /* ⭐ THE ISSUE MODEL'S INPUTS, computed once at page level.
     `derived` is the same call DerivedBodies makes, with the same registry
     argument — deliberately, so the fused and mis-carded rows the tab renders
     as `aria-disabled` are the SAME rows that get a control here. Two
     computations of one fact is how a surface comes to disagree with itself,
     which is the defect this page already warns about on its defect line. */
  const derived = useMemo(
    () => derivedBodies(data || [], {
      entries: REGISTRY_ENTRIES, labels: REGISTRY_LABELS, entityCount: REGISTRY_ENTITY_COUNT,
    }),
    [data],
  )
  const { byKey: verifyByKey, meta: verifyMeta, summary: verifySummary, error: verifyError } = useVerificationBodies()

  if (!allowed) {
    return (
      <>
        <PageHeader eyebrow="Reference" title="State Governing Bodies" />
        <div className={`card ${styles.section}`}>
          <p>{why?.(ACTIONS.CC_VIEW) || 'You do not have permission to view this.'}</p>
        </div>
      </>
    )
  }

  const withOthers = SOURCE.filter((b) => (b.others || []).length).length
  const isCollege = level === 'college'
  const isCanada = level === 'canada'
  const isPro = level === 'pro'
  /* ⚠️ A CHEER BODY HAS NO MEMBER SCHOOLS TO ROLL UP. Its competitors are
     all-star gyms and school programs, which is a different roster from the one
     `memberTotals` counts — so the member topline is hidden here exactly as it
     is for Professional, rather than printed as a confident zero. */
  const isCheer = level === 'cheer'
  /* The tab whose card list comes from the object rather than the registry. */
  const isDerived = level === 'derived'

  const setupChoice = assignmentCardChoice(hits)
  if (focusedSetup) return <>
    <PageHeader eyebrow="College Football" title="Bowl assignment setup" />
    {bowlReturnHref(params.get('returnTo')) && <Link className="btn" to={bowlReturnHref(params.get('returnTo'))}>Return to Bowl Eligibility</Link>}
    <BowlSetupGuide body={params.get('body') || ''} canShowCard={!!setupChoice.selected && complete && status === 'done'} onShowCard={() => pickBody(params.get('body'))} />
    {!params.get('body') && <label>Choose the conference<select value="" onChange={event => pickBody(event.target.value)}><option value="">Select a conference…</option>{allEntities(COLLEGE_BODIES).map(entity => <option key={entity.key} value={entity.name}>{entity.name}</option>)}</select></label>}
    {!params.get('body') ? null : status === 'error' ? <p role="alert">Conference records could not be read. This is not evidence that the conference has no records. Reload before making an assignment.</p>
      : status !== 'done' || !complete ? <p role="status">Loading the conference records before showing assignment choices…</p>
        : setupChoice.selected ? [setupChoice.selected].map(entity => <article className="card" key={entity.key} data-gb-card={entity.key} tabIndex={-1}>
            <h2>{entity.name} ({entity.acronym})</h2>
            {assignmentPermissionState === 'ready' && canManageAssignments && <BodyRoster entity={entity} rows={championshipsFor(entity, data).rows || []} initiallyOpen />}
          </article>) : setupChoice.choices.length ? <section className="card"><h2>Choose the intended conference</h2><p>This label matches more than one body. No assignment target has been selected.</p>{setupChoice.choices.map(entity => <button className="btn" key={entity.key} type="button" onClick={() => pickBody(entity.name)}>{entity.name}</button>)}</section> : <p role="status">No matching conference card was found. No assignment has been inferred. Open the governing-body directory to review the body name.</p>}
    {assignmentPermissionState === 'ready' && canManageAssignments && <RosterSetup />}
    <Link className="btn" to="/governing-bodies">Open the full governing-body directory</Link>
  </>

  return (
    <>
      {bowlReturnHref(params.get('returnTo')) && <Link className="btn" to={bowlReturnHref(params.get('returnTo'))}>Return to Bowl Eligibility</Link>}
      <PageHeader
        eyebrow="Reference"
        title={isDerived ? 'Bodies With No Registry Entry'
          : isCheer ? 'Cheer and Dance Governing Bodies'
          : isPro ? 'Professional Leagues'
          : isCanada ? 'Canadian Governing Bodies'
          : isCollege ? 'College Governing Bodies' : 'High School Governing Bodies'}
        subtitle={isDerived
          ? 'Carded from the object instead of from the registry. A body that holds records IS a body, whatever the registry says — so every governing-body label on a record gets a card here, and the four populations hiding in that number are named rather than treated as one.'
          : isCheer
          ? 'The bodies that sanction cheer and dance titles — all-star, school and international. Varsity Spirit alone holds more championship records than any other governing body SR has, including every state association.'
          : isPro
          ? 'The leagues whose championships are the largest single ring orders SR handles — one team, a hundred-odd rings.'
          : isCanada
            ? 'Canadian sport at both levels: U SPORTS for universities and the CCAA for colleges, each with its regional conferences. They are different levels, not one inside the other.'
            : isCollege
              ? 'Every association and conference that crowns a college championship we sell into — national, conference, Division I through III, plus the sport-specific bodies where a title actually lives.'
              : 'The association that sanctions every high-school state championship we sell into — and what we hold for that state today.'}
      />

      {/* ⭐ THE TRADES, ROLLED UP BY BODY (2026-10-19). Brian: "the trades
          should be surfaced in … governing bodies." Above the tabs on purpose:
          a trade's body can sit on any of the six tabs, and a roll-up that
          only counted the open tab would be a total that changes when you
          change tabs. It reconciles to every trade on the desk. */}
      <TradeRollup onPickBody={pickBody} />
      <RosterSetup />

      {/* ⭐ PERFECT GOVERNING BODIES (2026-10-19). Brian: "How many of the
          governing bodies are perfect now?" Above the tabs for the same reason
          as the trade roll-up: the answer is one number across every tab. */}
      <BodyMarketInventory data={data} status={status} complete={complete} onPickBody={pickBody} executive={role === 'exec'} />
      <BodyPerfection data={data} status={status} complete={complete} onPickBody={pickBody} />

      {/* The split itself. Buttons rather than a dropdown because every answer
          should be one tap away on a phone. */}
      <div className={styles.levelBar}>
        {[['hs', 'High school'], ['college', 'College'], ['canada', 'Canada'], ['pro', 'Professional'], ['cheer', 'Cheer and Dance'],
          /* ⭐ THE SIXTH TAB IS NOT A SIXTH REGISTRY. The other five are drawn
             FROM the registry and join records onto what they find, which is why
             a body the registry does not hold renders nowhere: it never enters
             the loop. This one is drawn from the RECORDS, so the object is the
             denominator and a body that holds records has a card whatever the
             registry says. The count is in the label because the whole point is
             that it is not zero. */
          ['derived', `No registry entry${blind.noCardBodies ? ` (${nf(blind.noCardBodies)})` : ''}`]].map(([k, label]) => (
          <button key={k} type="button" className={styles.levelBtn} data-on={level === k ? '1' : '0'}
            onClick={() => setLevel(k)}>{label}</button>
        ))}
      </div>

      {/* ⚠️ RENDERED HERE, NOT ON A PAGE OF ITS OWN. Body Coverage has listed
          these rows since 2.78 and the defect survived it, because a list on a
          reference page is not a card on the board: the banner below used to
          send the reader somewhere else to read about records this page could
          not hold. A tab beside the other five makes them reachable from the
          same place a rep already looks. */}
      {isDerived && (
        <DerivedBodies records={data || []} complete={complete} loading={status === 'loading'} focusBody={params.get('body') || ''} />
      )}

      {!isDerived && (
      <>

      {/* ⭐ SORT BY ASSIGNMENT. Brian: *"we should be able to sort by
          assignment."*
          Chips rather than a select, for the same reason the level bar is
          chips: every answer one tap away on a phone, and the state of the
          control readable without opening it.

          ⚠️ IT IS NOT `SortControls.jsx`. That component imports
          pursuit.module.css, and cc-mobile.css records that touching that file
          renames the live champion painter's hashed class and silently kills
          the chips it paints. This copies the worklist's `orderBar` instead,
          which is the in-house pattern that avoids the file entirely.

          🔴 `data-on` READS THE RESULT, NOT THE CLICK. `ordered.ordering.mode`
          is the mode the comparator ACTUALLY applied — so a chip can never
          claim an ordering the page did not perform, which is the pattern
          already asserted on the worklist. */}
      <div className={styles.orderBar} role="group" aria-label="What decides the card order">
        {SORT_MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            className={styles.sortChip}
            data-on={ordered.ordering.mode === m.key ? '1' : '0'}
            aria-pressed={ordered.ordering.mode === m.key}
            disabled={(m.key === 'assignment' || m.key === 'unassigned')
              && !ordered.ordering.assignmentAvailable}
            title={m.why}
            onClick={() => setSort((s) => ({ ...s, mode: m.key }))}
          >
            {m.label}
          </button>
        ))}
        <button
          type="button"
          className={styles.sortChip}
          data-dir={ordered.ordering.dir}
          aria-label={ordered.ordering.dir === 'asc' ? 'Reverse the order' : 'Reverse the order back'}
          onClick={() => setSort((s) => ({ ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' }))}
        >
          {ordered.ordering.dir === 'asc' ? 'Reverse ▼' : 'Reverse ▲'}
        </button>
      </div>
      {/* ⚠️ A CONTROL THAT REFUSED SILENTLY WOULD BE THE SAME DEFECT AS A BARE
          ZERO. If the two assignment sorts are unavailable, the page says which
          sort it used instead and why. */}
      {!ordered.ordering.assignmentAvailable && (
        <p className={styles.muted}>{ordered.ordering.whyNotAssignment}{' '}
          Showing the cards by name in the meantime.</p>
      )}

      {/* ⚠️ THE BLIND SPOT, STATED BY THE PAGE THAT HAS IT. Not a warning badge
          and not a percentage — a count and a door. Hidden only while the count
          is genuinely zero or the object has not loaded, because a banner that
          is always there stops being read. */}
      {blind.noCardBodies > 0 && (
        <div className={`card ${styles.blindSpot}`} role="note">
          <b>{nf(blind.noCardBodies)}</b> {BLIND_SPOT_LINE}{' '}
          <b>{nf(blind.noCard)}</b> records. {BLIND_SPOT_FIXED}{' '}
          <button type="button" className={styles.defectsBtn} onClick={() => setLevel('derived')}>
            Open the No registry entry tab
          </button>{' '}
          for a card per body, classified, with the arithmetic.{' '}
          <Link to="/body-coverage">Body Coverage</Link> is the same count as a flat list.
        </div>
      )}

      {/* ⭐ THE PAGE'S OWN PROBLEMS, EACH WITH THE CONTROL THAT ENDS IT.
          These are the issues no single card can hold — a record with no
          governing body belongs to no card by definition — which is exactly
          why they used to be unreachable: stated in a sentence, counted, and
          then left for somebody to go and find another tool for.

          ⚠️ IT ALSO PRINTS THE ASSERTION. "Every flag has a control" is
          computed and rendered here on every load, so the claim is checkable
          by whoever is looking rather than true on the day somebody walked it. */}
      {status !== 'loading' && (
        <BodyFixPanel
          records={data || []}
          totals={blind}
          orphans={orphans}
          derived={derived}
          complete={complete}
          verificationMeta={verifyMeta}
          verificationSummary={verifySummary}
          verificationError={verifyError}
        />
      )}

      <section className={`card ${styles.section}`}>
        <div className={styles.topline}>
          <div className={styles.counts}>
            <span><b>{SOURCE.length}</b> {level === 'hs' ? 'states + DC' : isPro ? 'league groups' : isCheer ? 'body groups' : 'associations'}</span>
            <span><b>{allEntities(SOURCE).length}</b> entities</span>
            <span><b>{withOthers}</b> with {level === 'hs' ? 'an independent or private body' : isPro ? 'leagues listed' : isCheer ? 'sanctioning bodies listed' : 'conferences listed'}</span>
            {!isPro && !isCheer && (
              <>
                <span><b>{nf(roll.members)}</b> member schools</span>
                <span><b>{roll.complete}</b> of {roll.bodies} rosters complete</span>
              </>
            )}
          </div>
          {/* ⚠️ TWO SEARCHES, BECAUSE THEY ANSWER TWO DIFFERENT QUESTIONS,
              AND ONLY ONE OF THEM EXISTED.

              Brian: "The lens did not make anything searchable… if I want to
              find every football championship within a governing body or
              within our entire universe, I should be able to do so."

              The box below searched BODY NAMES. Typing "football" into it
              asked whether there is an association called football, got no,
              and showed an empty board — while 833 football championships sat
              in the object. It is not wrong, it is answering a different
              question, so it keeps its job and says so on the label. */}
          <div className={styles.searches}>
            <input
              data-gb-search="1"
              className={styles.search}
              aria-label={level === 'hs' ? 'Find a state or association' : 'Find an association'}
              placeholder={level === 'hs' ? 'Find a state or association…' : isPro ? 'Find a league…' : isCheer ? 'Find a cheer or dance body…' : 'Find an association or conference…'}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <input
              className={styles.search}
              aria-label="Find a championship anywhere we hold one"
              placeholder="Find a CHAMPIONSHIP anywhere — football, cheer, rodeo…"
              value={champQ}
              onChange={(e) => setChampQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && champQ.trim()) setFinder({ query: champQ.trim() })
              }}
            />
            <button type="button" className={styles.searchBtn}
              disabled={!champQ.trim()}
              onClick={() => setFinder({ query: champQ.trim() })}>
              Search all {nf((data || []).length)} championships ›
            </button>
          </div>
        </div>
        {canPull && (
          <div className={styles.actions}>
            <button className="btn btn-primary" type="button" disabled={bulk.running} onClick={() => pullAll(SOURCE, level)}>
              {bulk.running ? `Pulling ${bulk.done} of ${bulk.total}…` : 'Pull every mark into HubSpot'}
            </button>
            <span className={styles.unverified}>{Object.keys(marks).length} hosted</span>
          </div>
        )}
        {/* ⚠️ ALWAYS LEAVES A SENTENCE. A control that completes in silence is
            indistinguishable from one that is broken. */}
        {bulk.note && <p className={styles.muted}>{bulk.note}</p>}
        {bulk.failed.length > 0 && (
          <p className={styles.warn}>
            {bulk.failed.length} could not be pulled — {bulk.failed.slice(0, 6).join(' · ')}
            {bulk.failed.length > 6 ? ' …' : ''}
          </p>
        )}
        {status !== 'loading' && (
          <p className={styles.muted}>
            <b>{nf(audits.sum)}</b> records with a blocking defect across the cards on this tab,{' '}
            <b>{nf(audits.distinct)}</b> distinct.
            {audits.sum > audits.distinct
              ? ` ${nf(audits.sum - audits.distinct)} are matched by more than one body and appear on each of their cards.`
              : ' No record is counted on two cards.'}
          </p>
        )}
        {/* ⭐ THE ASSIGNMENT ROLLUP FOR THIS TAB — shown + flagged = all.
            Brian: a body whose records span several reps needs an honest
            representation, and unassigned must be a visible state rather than
            an empty cell. So the page states the three figures and then states
            that they sum — and ships the branch where they do not. */}
        {status !== 'loading' && (
          <p className={styles.recon} role="status">
            <b>{nf(assignTotals.records)}</b> records on the{' '}
            <b>{nf(assignTotals.bodies)}</b> cards below:{' '}
            <b>{nf(assignTotals.assigned)}</b> name an assigned rep across{' '}
            <b>{nf(assignTotals.distinctReps)}</b> people, and{' '}
            <b data-bad={assignTotals.unassigned ? '1' : '0'}>{nf(assignTotals.unassigned)}</b>{' '}
            are <b>Unassigned</b>.{' '}
            {assignTotals.reconciled
              ? ASSIGNMENT_RECONCILED
              : <span className={styles.warn}>
                {ASSIGNMENT_RECONCILE_FAILED} {nf(Math.abs(assignTotals.shortfall))} records are
                counted on the cards and appear in neither column, so this breakdown is not
                trustworthy and the per-card stamps below may be wrong too.
              </span>}
            {' '}A record two bodies both claim is counted on each of their cards, so this total
            is larger than the object — the same caveat as the defect line above.
          </p>
        )}
        {status !== 'loading' && (
          <p className={styles.muted}>{ASSIGNMENT_CAVEAT}{' '}
            <Link to="/body-coverage">Body Coverage</Link> breaks the same assignment down by the
            label on the record rather than by the card.
          </p>
        )}
        {level === 'hs' && orphans.length > 0 && (
          <p className={styles.warn}>
            {nf(orphans.length)} state-level {orphans.length === 1 ? 'record names' : 'records name'} no state, so no card
            on this page can hold {orphans.length === 1 ? 'it' : 'them'}.{' '}
            <button type="button" className={styles.defectsBtn} onClick={() => setOrphansOpen(true)}>
              Open the state-missing records ›
            </button>
          </p>
        )}
        {/* ⚠️ SAYING WHAT THIS TAB CANNOT REACH. The cards below cover every
            cheer and dance record that names its sanctioning body. These do
            not name one — `sr_conference` reads "Unspecified" — so no card can
            ever hold them and no registry entry can fix that. Leaving the
            figure off would make the tab read as complete when it is not,
            which is the exact failure that hid cheer in the first place. */}
        {/* ⚠️ THE ANSWER TO "CAN WE PUT HIGH SCHOOL AND COLLEGE CHEER IN THE
            TAB TOO" IS THIS BUTTON, NOT A MOVE.
            A GHSA cheer title is crowned by GHSA. Relocating it here would
            make GHSA's own card under-report what GHSA crowns, and that number
            is what reps quote. The cards stay true to their bodies and the
            CATEGORY gets a lens across all of them instead. */}
        {isCheer && cheerUniverse.length > 0 && (
          <p className={styles.muted}>
            The cards below are the bodies whose whole business is cheer and dance.{' '}
            <b>{nf(cheerUniverse.length)}</b> cheer and dance championships exist across the
            object, and the rest are state and college titles that belong to the association that
            crowns them — a Georgia cheer title is a GHSA title and stays on GHSA&rsquo;s card.{' '}
            <button type="button" className={styles.defectsBtn} onClick={() => setFinder({ activity: 'Cheer and Dance' })}>
              Open all {nf(cheerUniverse.length)} across every body ›
            </button>
          </p>
        )}
        {/* ⚠️ THE RECORDS WITH NO BODY ARE NO LONGER BEHIND THEIR OWN DOOR.
            Brian: "There cannot be any invisible sports… flag it and say that
            it isn't complete, so we can get it rectified, because every
            championship needs to be pursued."

            A drawer of unknowns is a place records go to stop being looked at,
            and it made attribution a PRECONDITION of visibility. It is not:
            surface first, attribute second. So these now render inside the
            lens with every other cheer record, each carrying the flag
            "governing body not established", and this line is a shortcut to
            them rather than a separate list that can drift from the real one. */}
        {isCheer && unspecified.length > 0 && (
          <p className={styles.warn}>
            {nf(unspecified.length)} cheer and dance {unspecified.length === 1 ? 'record names' : 'records name'} no
            sanctioning body at all, so no card on this tab can hold {unspecified.length === 1 ? 'it' : 'them'}.
            {unspecified.length === 1 ? ' It is' : ' They are'} still in the lens, flagged, because
            {unspecified.length === 1 ? ' it is' : ' they are'} still pursuable — establishing the
            producer is a data repair, not a reason to hide the record.{' '}
            <button type="button" className={styles.defectsBtn} onClick={() => setFinder({ activity: 'Cheer and Dance' })}>
              Open them in the lens ›
            </button>
          </p>
        )}
        {/* ⚠️ THE LENS IS AVAILABLE ON EVERY TAB, NOT JUST CHEER.
            Brian: "it can also be done crossing every other governing body by
            sport or activity." Cheer was the first preset, never the
            mechanism, and a control that only appeared on one tab would have
            re-taught everyone that it was a cheer feature. */}
        <p className={styles.muted}>
          Looking for one sport rather than one body? The activity lens gathers a whole
          activity — wrestling, track &amp; field, esports, fishing, marching band, rodeo,
          robotics, cheer and dance — across every governing body at once, and flags every
          record in it that is still missing a body, a date, a champion or a state.{' '}
          <button type="button" className={styles.defectsBtn}
            onClick={() => setFinder({ activity: isCheer ? 'Cheer and Dance' : '' })}>
            Open the activity lens ›
          </button>
        </p>
        {status === 'loading' && <p className={styles.muted}>Reading our records — {nf(loadedCount)} so far.</p>}
        {status !== 'loading' && !complete && (
          <p className={styles.warn}>Only {nf(loadedCount)} records loaded, so the per-state counts below are partial.</p>
        )}
        {/* Said plainly rather than left for someone to discover by clicking. */}
        <p className={styles.muted}>
          Marks are SR-set type until a logo is licensed and hosted by us. These are the
          associations&rsquo; trademarks, and linking them from their own websites would put a
          dead-link-in-waiting on our board — the same rule that keeps borrowed school crests off
          Top Tier Teams. Pulling one fetches the file server-side and hosts it in HubSpot File
          Manager, so what the board renders is always ours.
        </p>
        {/* ⚠️ Absence here is NOT evidence. Saying nothing about a state's private
            associations is different from saying it has none, and the second reading
            is how a whole category of championships stays invisible. */}
        <p className={styles.muted}>
          Every site and logo below was fetched and confirmed to load on 1 September 2026. Eight of
          the public-body URLs were wrong on the first pass — including DC&rsquo;s, whose old domain
          has lapsed and now serves a gambling site. A state marked <b>searched</b> was checked for
          independent associations and none was found; that is not the same as proof none exists.
        </p>
      </section>

      <div className={styles.grid}>
        {/* ⚠️ THE ORDER IS THE MODEL'S, AND THE ASSIGNMENT COMES WITH IT. The
            pairs carry their own `assignment` so the loop never recomputes one,
            and the chip above is reading the same `ordering` block. */}
        {ordered.pairs.map(({ entity: e, assignment: assign }) => {
          const hosted = marks[e.key]
          /* A HubSpot-hosted mark is the only thing that renders as an image. */
          const mark = hosted ? { kind: 'logo', src: hosted.url } : markFor(e)
          /* ⚠️ A ROLL-UP CARD COUNTS ITS CHILDREN, NOT ITSELF.
             The cheer tab's heading card is not a governing body — it is the
             tab. Matching it like a body gave the board two different answers
             for the same thing, which is what Brian caught: 1,454 against
             Varsity Spirit's 1,355. Its number is now the de-duplicated union
             of the real bodies beside it, so it cannot drift from them. */
          /* ⚠️ THE SIBLINGS ARE THE WHOLE TAB, NOT THE SEARCH RESULTS, AND THAT
             IS A CORRECTION. This passed the FILTERED `entities`, so typing in
             the search box changed the roll-up's own total — the heading card
             for the tab would report fewer records than the tab holds because
             somebody was looking for one body. A roll-up IS the tab, so its
             number is a fact about the tab, which is the same reasoning
             `peerBaseline` above already states. It also keeps this figure
             identical to the one `assignmentByKey` computed, so the assignment
             stamp below cannot disagree with the number above it under search. */
          const tally = e.rollup
            ? rollupFor(e, allEntities(SOURCE), data || [])
            : championshipsFor(e, data)
          const zero = e.rollup ? null : zeroKind(e, data || [])
          /* ⚠️ THE NON-ZERO THAT IS STILL WRONG. `zeroKind` stops a card showing
             a bare 0; it said nothing about the Pac-12 showing a bare 6 beside
             the SEC's 42. Both are a card claiming completeness it does not
             have, and only one of them was being caught. */
          const coverage = e.rollup ? null : coverageSignal(e, data || [], baseline)
          const held = audits.byKey.get(e.key)
          /* ⚠️ THE TEAMS GO ON THE CARD, NOT THREE CLICKS DEEP.
             Brian: "i cannot see any teams that are in the conferences - so the
             ability for our system to crown a school with a sport and be able
             to source the point of contact or coach to actually reach out to
             will not work."
             The chain is championship -> SCHOOL -> sport -> coach -> outreach,
             and it breaks at the first link if the schools are invisible. They
             were rendered, but inside a COLLAPSED section, inside a drawer,
             behind a click on the stats block. Shipped and unreachable is the
             same as absent. */
          /* ⚠️ ONE RESOLVER, EVERY LEVEL. This used to ask sportAffiliation —
             the COLLEGE roster — for a high-school body's members, so every HS
             card got nothing and rendered "Teams not sourced yet" while 22,894
             harvested schools sat unused in hsRosters. The level picks the
             source now, and `bodyMembers.js` is the only thing that knows how.

             ⚠️ A PARTIAL ROSTER IS NOT A ROSTER. An unsourced conference still
             shows any school that happens to appear as a DEPARTURE, which is
             how Big South rendered "1 team" when it has nine. Saying so is the
             difference between a gap and a lie. */
          const mem = membersFor(e, level)
          const members = mem.members
          const complete = mem.complete
          /* ⭐ EVERY PROBLEM ON THIS CARD, EACH CARRYING ITS CONTROL.
             Handed the SAME `tally`, `assign`, `zero` and `coverage` the card
             renders above — never re-derived. A second join is a second answer
             to one question, and the breakdown disagreeing with the number six
             lines above it on the same card is the defect this surface exists
             to remove. */
          const issues = cardIssues({
            entity: e,
            rows: tally.rows || [],
            assign,
            tally,
            zero,
            coverage,
            defects: held?.blocking ?? 0,
            verification: verificationFor(verifyByKey, e, tally.rows || []),
            hosted: Boolean(hosted),
          })
          return (
            <article key={e.key} data-gb-card={e.key} tabIndex={-1} className={`card ${styles.cardItem}`} style={{ '--accent': e.accent }}>
              <div className={styles.head}>
                {mark.kind === 'logo'
                  ? <img className={styles.logo} src={mark.src} alt={`${e.acronym} logo`} />
                  : <div className={styles.mark} aria-hidden="true">{mark.text}</div>}
                <div className={styles.title}>
                  <h2 className={styles.acr}>
                    {e.acronym}
                    {!e.primary && <span className={styles.second}>{e.kind || 'second'} association</span>}
                  </h2>
                  <p className={styles.state}>{e.state}</p>
                </div>
              </div>

              <p className={styles.name}>{e.name}</p>

              {/* A structural quirk that breaks the one-body assumption is the
                  most valuable thing on the card — Iowa, New York City, CIF. */}
              {e.note && <p className={styles.note}>{e.note}</p>}
              {e.evidence && <p className={styles.note}>{e.evidence}</p>}

              <div className={styles.flags}>
                {/* ⚠️ A multi-state body's title is NOT a state championship —
                    a NEPSAC or MAIS winner did not win their state. */}
                {e.multiState && <span className={styles.flagWarn}>multi-state title</span>}
                {e.dormant && <span className={styles.flagWarn}>dormant</span>}
                {e.noSite && <span className={styles.flagWarn}>no website — exists, unlinkable</span>}
                {e.insecure && <span className={styles.flagMuted}>http only</span>}
                {e.primary && e.searched && <span className={styles.flagMuted}>searched for independents</span>}
                {!e.logoSource && <span className={styles.flagMuted}>no logo published</span>}
              </div>

              {/* ⚠️ COUNTS ON EVERY CARD, NOT JUST THE PRIMARY HIGH-SCHOOL ONES.
                  This block used to be gated on `e.primary` and matched on state
                  name, so every COLLEGE conference — the ACC, the Big Ten, the
                  NCAA itself — rendered with no numbers at all. Half the board
                  was a directory of names and outbound links to somebody else's
                  website, which is the opposite of what a card here is for.
                  `championshipsFor` joins on state for a state body and on the
                  record's own conference field for a conference. */}
              <button type="button" className={styles.statsBtn} onClick={() => setOpen(e)}>
                <span className={styles.stats}>
                  <span><b>{nf(tally.held)}</b><span>championships</span></span>
                  <span><b>{nf(tally.crowned)}</b><span>crowned</span></span>
                  <span><b data-bad={tally.undated ? '1' : '0'}>{nf(tally.undated)}</b><span>no date</span></span>
                  <span><b data-bad={held?.blocking ? '1' : '0'}>{nf(held?.blocking ?? 0)}</b><span>defects</span></span>
                </span>
                <span className={styles.statsGo}>See what they crown ›</span>
              </button>

              {/* ⭐ THE ASSIGNMENT STAMP. Brian: *"every governing body should
                  include a stamp of who is assigned"*, and *"a body whose
                  records span several reps needs an honest representation —
                  show the owners, or the dominant owner with a count, but never
                  pick one arbitrarily and never render blank."*

                  🔴 SO IT NEVER PRINTS A BARE NAME OVER A SPLIT BODY. Four
                  states, four different sentences, and the shared and part-
                  assigned ones carry the dominant rep's COUNT AND SHARE on the
                  face with the full list one tap behind it — a top name on its
                  own would read as sole holder, which on a body split five ways
                  is the same small lie as the SPIRIT roll-up disagreeing with
                  Varsity Spirit's own count — one card, two answers.

                  ⚠️ `Unassigned` IS A VALUE, STYLED AS THE GAP IT IS
                  (`data-gap="1"` — the convention BodyChampionships.jsx already
                  uses), never an empty cell. It is also what the sort reaches
                  from one direction. */}
              <p className={styles.assign} data-state={assign.state}>
                <span className={styles.assignLabel}>Assigned</span>
                {assign.state === 'empty' && (
                  <span className={styles.assignValue} data-gap="1">
                    No records on this card yet, so there is nothing to assign — read the zero
                    above for which kind of zero that is.
                  </span>
                )}
                {assign.state === 'unassigned' && (
                  <span className={styles.assignValue} data-gap="1">
                    Unassigned — all {nf(assign.records)}{' '}
                    {assign.records === 1 ? 'record' : 'records'}, and not one of them names a rep.
                  </span>
                )}
                {assign.state === 'single' && (
                  <span className={styles.assignValue}>
                    {assign.dominant.rep} — all {nf(assign.records)}{' '}
                    {assign.records === 1 ? 'record' : 'records'}.
                  </span>
                )}
                {assign.state === 'shared' && (
                  <span className={styles.assignValue}>
                    {assign.dominant.rep} ({nf(assign.dominant.count)} of {nf(assign.records)},{' '}
                    {Math.round(assign.dominant.share * 100)}%) — shared with{' '}
                    {assign.distinctReps - 1} {assign.distinctReps === 2 ? 'other' : 'others'}.
                  </span>
                )}
                {assign.state === 'partial' && (
                  <span className={styles.assignValue}>
                    {assign.dominant.rep} ({nf(assign.dominant.count)} of {nf(assign.records)},{' '}
                    {Math.round(assign.dominant.share * 100)}%){' · '}
                    <b data-gap="1">{nf(assign.unassigned)} unassigned</b>
                    {assign.distinctReps > 1 ? ` · ${assign.distinctReps} people in total.` : '.'}
                  </span>
                )}
                {/* The full list, one tap behind the face. Rendered for every
                    card that has more than one person on it, in count order —
                    so the breakdown is reachable and never implied. */}
                {assign.reps.length > 1 && (
                  <span className={styles.assignMore}>
                    {assign.reps.slice(1).map((r) => `${r.rep} ${nf(r.count)}`).join(' · ')}
                  </span>
                )}
                {/* 🔴 THE TWO NUMBERS MUST AGREE, AND IT SAYS SO WHEN THEY DO
                    NOT. The stamp counts the SAME rows as the championships
                    figure above, so this can only fire if the join drifted —
                    and a breakdown that silently disagrees with the number
                    beside it is the defect this stamp was asked to fix. */}
                {(assign.records !== tally.held || !assign.reconciled) && (
                  <span className={styles.assignFail}>
                    {ASSIGNMENT_RECONCILE_FAILED} {ASSIGNMENT_CARD_MISMATCH}{' '}
                    ({nf(assign.records)} against {nf(tally.held)}).
                  </span>
                )}
              </p>

              {/* ⭐ SETTING THE OWNER, WHERE THE OWNER IS READ.
                  Brian: *"in governing body - i need to be able to add a rep to
                  a governing body."* He works this card by card, so the control
                  belongs on the card rather than behind a hunt for records.

                  ⚠️ IT TAKES `tally.rows` — THE SAME ROWS THE STAMP ABOVE
                  COUNTS. A second join would be a second answer to one
                  question, and the panel's "on the records today" line would
                  disagree with the stamp six lines above it on the same card.
                  That is the reconciliation failure this whole surface was
                  built to remove, so the rows are passed down rather than
                  re-derived.

                  ⚠️ IT WRITES THE ASSIGNMENT MAP, NOT THESE RECORDS, and says
                  so on its face. A rep typed onto a record is an owner the map
                  cannot explain, and it comes back as a conflict on every
                  future apply-map run. See BodyRoster.jsx.

                  ⚠️ NOT GATED ON `isCollege` OR ON THE TAB. Bodies on every tab
                  need owners — the Pac-12 defect was a card that existed and
                  could not be reached, and a control that only appears on high
                  school is the same shape of mistake one level down. */}
              <BodyRoster entity={e} rows={tally.rows || []} />

              {/* ⭐ THE TRADES ON THIS CARD (2026-10-19), over the SAME
                  `tally.rows` the count and the stamp above use — so a card can
                  never say "12 records" and show trades over a different 12.
                  Renders nothing when no trade touches this body. */}
              <BodyTrades rows={tally.rows || []} />

              {/* ⭐ AND THE REST OF THIS CARD'S PROBLEMS, EACH WITH ITS DOOR.
                  The sentences below used to render WITHOUT these controls —
                  "Unassigned — all 80 records", "This looks incomplete",
                  "RECONCILIATION FAILED", "N records with a defect" — every one
                  of them a statement that something is wrong with no way to act
                  on it. Brian: a flag you cannot act on trains people to ignore
                  the board.

                  ⚠️ THE LOOP DOES NOT CHOOSE THE BUTTONS. It walks the controls
                  the model resolved. A renderer that picked its own could drop
                  one for a single kind and nothing anywhere would fail. */}
              {issues.map((i) => (
                <IssueFix key={i.id} issue={i} context={{ entity: e, records: i.records }} />
              ))}

              {/* ⚠️ ITS OWN CONTROL, OUTSIDE the stats button. Nested buttons are
                  invalid and the defect list is a different question from
                  "what do they crown" — one is the worklist, the other the
                  catalog. */}
              {/* ⚠️ EVERY TAB, NOT JUST HIGH SCHOOL. This was gated on
                  `!isCollege` because the defect count came from a STATE bucket
                  and a conference has no state — so college, Canada and pro had
                  no way in at all. DefectPanel now scopes by entity, so the
                  door is the same everywhere. The count still comes from the
                  state audit where there is one; elsewhere the panel counts its
                  own rows. */}
              {tally.total > 0 && (
                <button type="button" className={styles.defectsBtn}
                  onClick={() => setDefectsOpen(e)}>
                  {held?.blocking > 0
                    ? `Show the ${nf(held.blocking)} ${held.blocking === 1 ? 'record' : 'records'} with a defect ›`
                    : 'Check this body\u2019s records \u203a'}
                </button>
              )}

              {/* ⚠️ NO CARD IS ALLOWED TO SHOW A BARE ZERO.
                  Brian, with a screenshot: "There are still governing bodies
                  that show zero championships which is unacceptable."

                  A 0 was ambiguous between three completely different problems
                  needing opposite work — we never acquired them (ASUN), the
                  records exist and the card could not see them (NCAA, 275 deep)
                  or the records exist and are truncated junk (NEC, 25 records
                  whose entire name is the string "NEC"). A rep reading the bare
                  number concluded "nothing here" in all three cases, which is
                  right once and badly wrong twice. */}
              {zero && (
                <p className={styles.zeroKind} data-kind={zero.kind}>
                  <b>Zero, and here is which kind:</b> {zero.label}. {zero.detail}
                </p>
              )}

              {/* ⚠️ AND NO CARD IS ALLOWED TO LOOK FINE WHILE BEING OBVIOUSLY
                  INCOMPLETE. This is the signal that would have saved the
                  presentation — not a full Pac-12, but a board that ADMITTED the
                  Pac-12 was thin. Six championships rendered exactly like
                  forty-two, so there was nothing to read and nothing to doubt.
                  The comparison is to the median of the same KIND of body, and
                  the number is stated as an estimate because that is what it
                  is. */}
              {coverage && (
                <p className={styles.zeroKind} data-kind="thin">
                  <b>This looks incomplete:</b> {coverage.label}. {coverage.detail}
                </p>
              )}

              {/* Search inside this one body — the scoped half of Brian's ask:
                  "find every football championship WITHIN a governing body". */}
              {tally.total > 0 && (
                <button type="button" className={styles.defectsBtn}
                  onClick={() => setFinder({ entity: e, scope: 'body', query: '' })}>
                  Search inside {e.acronym} ›
                </button>
              )}

              {members.length > 0 ? (
                <div className={styles.teams}>
                  <span className={styles.teamsHead} data-partial={complete ? '0' : '1'}>
                    {complete
                      ? `${members.length} ${members.length === 1 ? 'team' : 'teams'} compete here`
                      : `${members.length} known — full roster not sourced yet`}
                  </span>
                  <span className={styles.teamList}>
                    {members.slice(0, 8).map((m) => (
                      <span key={m.name} className={styles.team} data-only={m.only ? '1' : '0'}
                        title={m.only ? `${m.name} is here for ${m.only} only` : m.name}>
                        {m.name}
                        {/* A school here for ONE SPORT is marked, because
                            crowning it in any other sport would be wrong. */}
                        {m.only && <i>{m.only}</i>}
                      </span>
                    ))}
                    {members.length > 8 && (
                      <button type="button" className={styles.teamMore} onClick={() => setOpen(e)}>
                        +{members.length - 8} more
                      </button>
                    )}
                  </span>
                </div>
              ) : (
                /* ⚠️ AN HONEST BLANK. Saying nothing here would read as "this
                   conference has no teams", which is never true — it means we
                   have not sourced them yet, and that is a worklist item. */
                <p className={styles.teamsNone}>Teams not sourced yet — a champion here cannot be matched to a school.</p>
              )}

              <div className={styles.actions}>
                {e.site
                  ? <a className="btn" href={e.site} target="_blank" rel="noopener noreferrer">Official site</a>
                  : <span className={styles.unverified}>no site to link</span>}
                {/* Never presented as checked when it has not been. */}
                {e.champs && (
                  <a className="btn" href={e.champs} target="_blank" rel="noopener noreferrer">Championships</a>
                )}
                {canPull && !hosted && (
                  <button className="btn" type="button"
                    onClick={() => setPull({ key: e.key, url: '', busy: false, error: '', ok: '' })}>
                    Add mark
                  </button>
                )}
              </div>

              {canPull && pull.key === e.key && (
                <div className={styles.pull}>
                  <input
                    className={styles.search}
                    placeholder="https://…/logo.png — link straight to the image file"
                    value={pull.url}
                    onChange={(ev) => setPull((p) => ({ ...p, url: ev.target.value }))}
                  />
                  <div className={styles.actions}>
                    <button className="btn btn-primary" type="button" disabled={!pull.url || pull.busy}
                      onClick={() => ingest(e.key, e.acronym)}>
                      {pull.busy ? 'Pulling…' : 'Pull into HubSpot'}
                    </button>
                    <button className="btn" type="button" onClick={() => setPull({ key: '', url: '', busy: false, error: '', ok: '' })}>Cancel</button>
                  </div>
                  {pull.error && <p className={styles.warn}>{pull.error}</p>}
                </div>
              )}
            </article>
          )
        })}
      </div>

      {open && (
        <BodyChampionships entity={open} universe={data} onClose={() => setOpen(null)} />
      )}

      {defectsOpen && (
        <DefectPanel entity={defectsOpen} universe={data} onClose={() => setDefectsOpen(null)} />
      )}

      {orphansOpen && (
        <DefectPanel entity={{ acronym: 'State missing', name: 'State-level records with no state' }}
          rows={orphans} onClose={() => setOrphansOpen(false)} />
      )}

      {/* ⚠️ THE LENS IS HANDED THE WHOLE OBJECT, NOT A PRE-FILTERED SLICE.
          Filtering on the way in is how a record stops existing for a view
          that claims to cover its activity — and "there cannot be any
          invisible sports". The lens does its own membership test, shows what
          each record is missing, and reconciles its own totals. */}
      {finder && (
        <ChampionshipFinder
          universe={data || []}
          entity={finder.entity || null}
          initialScope={finder.scope || 'universe'}
          initialQuery={finder.query || ''}
          initialActivity={finder.activity || ''}
          onClose={() => setFinder(null)} />
      )}


      {/* ⚠️ THIS BLOCK USED TO BE THE BARE ZERO, AND IT IS THE ONE BRIAN READ.
          It said, in full: `No association matches "PAC12"`. That is a sentence
          shaped like an answer and it carried no information — indistinguishable
          from "this conference does not exist", which is the conclusion he drew,
          out loud, about a conference we hold championships for and had assigned
          to a rep. Every other surface here is already forbidden from rendering
          an unexplained zero: `zeroKind` names which of three kinds a 0 is, and
          `assignmentViews` returns `touchedPct: null` WITH its reason attached
          rather than a 0. The search box was the hole left in that rule.

          So it now names the term that excluded everything and offers the
          closest bodies as buttons that run the search. */}
      {entities.length === 0 && (
        <div className={`card ${styles.section}`}>
          <p className={styles.warn}>
            Nothing on the {LEVEL_LABEL[level]} tab matches “{q}” — and here is why:{' '}
            {noMatch?.culpritReason || 'the query matched no name, acronym or state.'}
          </p>
          {noMatch?.suggestions?.length > 0 ? (
            <p className={styles.muted}>
              Closest {noMatch.suggestions.length === 1 ? 'match' : 'matches'} we do hold:{' '}
              {noMatch.suggestions.map((s, i) => (
                <span key={s.entity.key}>
                  {i > 0 && ' · '}
                  <button type="button" className={styles.defectsBtn} onClick={() => setQ(s.entity.acronym)}>
                    {s.entity.acronym} — {s.entity.name}
                  </button>
                </span>
              ))}
            </p>
          ) : (
            /* ⚠️ "NO SUGGESTION" IS ITSELF AN ANSWER AND HAS TO BE SAID. Saying
               nothing here would put the bare zero straight back. */
            <p className={styles.muted}>
              No body on this tab is even close to that spelling. It may be on another
              tab — this one is {LEVEL_LABEL[level]} only — or we may genuinely not
              hold it yet, which is an acquisition gap rather than a search problem.
            </p>
          )}
          <p className={styles.muted}>
            Punctuation, spacing and case are ignored, so “PAC12”, “pac 12” and “Pac-12”
            are the same search. Former names resolve to current ones.
          </p>
        </div>
      )}

      {/* A card reached through a DEAD NAME is explained rather than just shown,
          because an unfamiliar card is how the American came to be written up as
          holding zero records twice. */}
      {renamed && entities.length > 0 && (
        <div className={`card ${styles.section}`}>
          <p className={styles.muted}>
            “{renamed.was}” is now <b>{renamed.now}</b>
            {renamed.when ? ` (renamed ${renamed.when})` : ''}. Showing it under its current
            name — records filed under the old one are filed under a dead name.
          </p>
        </div>
      )}
      </>
      )}
    </>
  )
}
